import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Check, AlertTriangle, Activity, Users, Package, Search } from 'lucide-react';
import { api } from '../lib/api';
import clsx from 'clsx';

export default function CategoryDrilldown({ categoryName, onClose, onProductClick, dateRange, filters }) {
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['category-products', categoryName, dateRange, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('days', dateRange?.days || 7);
      params.set('limit', '50');
      if (filters?.campusId) params.set('campusId', filters.campusId);
      if (filters?.userType) params.set('userType', filters.userType);
      if (filters?.visitMetric) params.set('visitMetric', filters.visitMetric);
      return await api.get(`/analytics/category/${encodeURIComponent(categoryName)}/products?${params.toString()}`);
    },
    enabled: !!categoryName
  });

  if (!categoryName) return null;

  const filteredProducts = data?.products?.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.domain?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-4">
            {isLoading ? (
              <div className="animate-pulse">
                <div className="h-6 bg-surface-100 dark:bg-surface-700 rounded w-48 mb-2" />
                <div className="h-4 bg-surface-100 dark:bg-surface-700 rounded w-24" />
              </div>
            ) : (
              <>
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: data?.category?.color || '#64748b' }}
                />
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{data?.category?.name || categoryName}</h2>
                  <p className="text-slate-400 text-sm">
                    {data?.stats?.productCount || 0} products in this category
                  </p>
                </div>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] space-y-6">
          {error ? (
            <div className="text-center py-12 text-red-400">
              Failed to load category details
            </div>
          ) : isLoading ? (
            <div className="space-y-4 animate-pulse">
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-surface-100 dark:bg-surface-700 rounded-lg" />
                ))}
              </div>
              <div className="h-10 bg-surface-100 dark:bg-surface-700 rounded-lg" />
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-16 bg-surface-100 dark:bg-surface-700 rounded-lg" />
              ))}
            </div>
          ) : (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-2">
                    <Activity className="w-4 h-4" />
                    Total Visits
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    {data?.stats?.totalVisits?.toLocaleString() || 0}
                  </div>
                </div>
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-2">
                    <Users className="w-4 h-4" />
                    Unique Devices
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    {data?.stats?.uniqueDevices?.toLocaleString() || 0}
                  </div>
                </div>
                <div className="bg-surface-100 dark:bg-surface-800 rounded-lg p-4 border border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-2">
                    <Package className="w-4 h-4" />
                    Products
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white">
                    {data?.stats?.productCount || 0}
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>

              {/* Products List */}
              <div className="space-y-2">
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    {searchTerm ? 'No products match your search' : 'No products in this category'}
                  </div>
                ) : (
                  filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => {
                        onProductClick?.(product.id);
                        onClose();
                      }}
                      className={clsx(
                        'w-full text-left p-4 bg-surface-100 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700',
                        'hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors',
                        'flex items-center justify-between group'
                      )}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate text-slate-900 dark:text-white">{product.name}</span>
                            {product.isApproved ? (
                              <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-slate-500 font-mono truncate">
                            {product.domain}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 text-sm flex-shrink-0">
                        <div className="text-right">
                          <div className="font-semibold">{product.visits.toLocaleString()}</div>
                          <div className="text-slate-500 text-xs">visits</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">{product.devices.toLocaleString()}</div>
                          <div className="text-slate-500 text-xs">devices</div>
                        </div>
                        <div className="w-6 text-slate-500 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                          →
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

